import {
  DealItem,
  DealItemApi,
  DealTargetApi,
  IDealPricesList,
  IDealPricesListApi,
  IDetailsDeal,
  IDetailsDealApi,
  IDetailsDiscountsApi,
} from 'types/models/Deals/deals.schema';
import { GlobalVariables } from 'config/constant';
import {
  transformItemArticleResponse,
  transformMediaArticle,
  transformPcbObjectArticle,
  transformPricesObject,
} from 'redux/api/article/articlesApi.transform';
import {
  IDiscount,
  IDiscountApi,
  IDiscountPackingPricesList,
  IDiscountPricesForResume,
  IDiscountPricesListApi,
  IDiscountResponse,
  IDiscountResponseApi,
} from 'types/models/Discount/discounts.schema';
import { Article, Discount, IDiscountPricesApi, IPackingPrice } from 'types/models/Article/article';
import {
  cToNumber,
  formattedDigitNumber,
  getDiscountPercentage,
  getPriceAfterDiscount,
} from 'utils/helpers/number.helpers';
import {
  DealTypeEnumApiValues,
  LabelPcbEnum,
  ProductNatureEnum,
  ProductTypeEnum,
} from 'types/interfaces/sharedType.type';
import { isEmpty } from 'lodash';
import { IResumeTableResult } from 'features/Deals/AddDeal/AddDealForm/AddDealForm.type';
import { compareAndTransformValue, extractPackingTypeLabel } from 'utils/helpers/string.helpers';
import { PackingTypeEnum } from 'types/enum/packing-type.enum';

function transformDealTarget(data: DealTargetApi[]) {
  const transformed = data?.map((target) => {
    return {
      id: target?.id,
      zone: {
        id: target?.zone?.id,
        nameAr: target?.zone?.nameAr,
        nameFr: target?.zone?.nameFr,
        status: target?.zone?.status === GlobalVariables.STATUS.ACTIVE,
        deletedAt: target?.zone?.deletedAt,
      },
      clientCategories: target?.clientCategories?.map((category) => {
        return {
          id: category?.id,
          nameAr: category?.nameAr,
          nameFr: category?.nameFr,
          status: category?.status === GlobalVariables.STATUS.ACTIVE,
          sageId: category?.sageId,
          minimumOrderPrice: category?.minimumOrderPrice,
          deletedAt: category?.deletedAt,
        };
      }),
      clientClasses: target?.clientClasses?.map((clientClass) => {
        return {
          id: clientClass?.id,
          nameAr: clientClass?.nameAr,
          nameFr: clientClass?.nameFr,
          status: clientClass?.status === GlobalVariables.STATUS.ACTIVE,
        };
      }),
      clients: target?.clients?.map((client) => {
        return {
          id: client?.id,
          deletedAt: client?.deletedAt,
          phoneNumber: client?.phoneNumber,
          firstName: client?.firstName,
          lastName: client?.lastName,
          email: client?.email,
          cin: client?.cin,
          sageCode: client?.sageCode,
          clientRisk: client?.clientRisk,
          createdAt: client?.createdAt,
          paymentMethod: client?.paymentMethod,
          clientStatus: client?.clientStatus,
          activationStatus: client?.activationStatus === GlobalVariables.STATUS.ACTIVE,
          budgetLimit: client?.budgetLimit,
          currentBudgetLimit: client?.currentBudgetLimit,
          defaultShop: {
            id: client?.defaultShop?.id,
            name: client?.defaultShop?.name,
            companyName: client?.defaultShop?.companyName,
            latitude: client?.defaultShop?.latitude,
            longitude: client?.defaultShop?.longitude,
            taxRegistrationNumber: client?.defaultShop?.taxRegistrationNumber,
          },
          categoryId: client?.categoryId,
        };
      }),
      sectors: target?.sectors?.map((sector) => {
        return {
          id: sector?.id,
          nameAr: sector?.nameAr,
          nameFr: sector?.nameFr,
          status: sector?.status === GlobalVariables.STATUS.ACTIVE,
          deletedAt: sector?.deletedAt,
          profile: sector?.profile,
          attractionAbility: sector?.attractionAbility,
        };
      }),
      deletedAt: target?.deletedAt,
    };
  });
  // move the first complete configuration to the top of the array
  const indexOfCompleteConfig = transformed?.findIndex(
    (item) =>
      (item.clientCategories && item.clientCategories.length > 0) ||
      (item.clientClasses && item.clientClasses.length > 0) ||
      (item.clients && item.clients.length > 0),
  );

  if (indexOfCompleteConfig > 0 && transformed) {
    const [found] = transformed.splice(indexOfCompleteConfig, 1);
    transformed.unshift(found);
  }
  return transformed;
}

function transformDealsItems(data: DealItemApi[]): DealItem[] {
  return data?.map((item) => {
    return {
      id: item?.id,
      type: item?.type,
      discount: item?.discount,
      packingType: item?.packingType,
      discountedPrice: item?.discountedPrice,
      originalPrice: item?.originalPrice,
      quantity: item?.quantity,
      article: transformItemArticleResponse(item?.article) as Article,
    };
  });
}

export const transformDealsDetails = (data: IDetailsDealApi): IDetailsDeal => {
  return {
    id: data?.id,
    nameFr: data?.nameFr,
    nameAr: data?.nameAr,
    dealStock: data?.dealStock,
    status: data?.status === GlobalVariables.STATUS.ACTIVE,
    dealEndDate: data?.dealEndDate,
    dealStartDate: data?.dealStartDate,
    descriptionFr: data?.descriptionFr,
    descriptionAr: data?.descriptionAr,
    brand: data?.brand,
    catalogs: data?.catalogs,
    dealType: data?.dealType,
    dealRemainingStock: data?.dealRemainingStock,
    deletedAt: data?.deletedAt,
    name: data?.name,
    type: data?.type,
    ean13Code: data?.ean13Code,
    sageLabel: data?.sageLabel,
    sageReference: data?.sageReference,
    description: data?.description,
    media: transformMediaArticle(data?.media),
    pcbsArray: transformPcbObjectArticle(data?.pcbsObject),
    pricesObject: transformPricesObject(data?.pricesObject),
    dealTargets: transformDealTarget(data?.dealTargets),
    dealItems: transformDealsItems(data.dealItems),
    dealWarehouse: data?.dealWarehouse,
  };
};

export const transformDiscountResponse = (data: IDiscountResponseApi): IDiscountResponse => {
  return {
    lastPage: data?.lastPage,
    totalPerPage: data?.totalPerPage,
    nextPage: data?.nextPage,
    total: data?.total,
    previousPage: data?.previousPage,
    data: data?.results?.map((item) => transformDiscountItemResponse(item)),
  };
};

const transformDiscountItemResponse = (data: IDiscountApi): IDiscount => {
  return {
    id: data?.id,
    createdAt: data?.createdAt,
    discountPackingPrices: data?.discountPackingPrices?.map((item) => {
      return {
        type: item?.discountPackingPrices?.[0]?.type,
        percentage: item?.discountPackingPrices?.[0]?.percentage,
        warehouseId: item?.warehouseId,
        warehouse: item.warehouse,
      };
    }),
    discountPrices: data?.discountPrices,
    updatedAt: data?.updatedAt,
    startDate: data?.startDate,
    endDate: data?.endDate,
    nameFr: data?.nameFr,
    discountStocks: data?.discountStocks,
    discountRemainingStocks: data?.discountRemainingStocks,
    status: data?.status === GlobalVariables.STATUS.ACTIVE,
    canShowTag: data?.canShowTag ?? false,
    warehouses: data?.warehouses,
  };
};
const getOriginalPrice = (data: IPackingPrice[] | undefined, discountType: string) =>
  data ? data.find((item) => item.type === discountType)?.amount ?? 0 : 0;
const getDiscountedPrice = (data: IPackingPrice[], discountType: string, discount: number) =>
  getPriceAfterDiscount(getOriginalPrice(data, discountType), discount);
export const transformDiscountPrices = (
  data: IDiscountPricesApi[] | undefined,
): { data: DealItem[]; numberToAddToTotal: number } => {
  if (!data || isEmpty(data)) return { data: [], numberToAddToTotal: 0 };
  let numberToAddToTotal = 0; // This is used to add the index of the item to the total count
  const dataToReturn = data
    .map((item) => {
      return item?.discountPackingPrices?.map((discountItem, index) => {
        numberToAddToTotal += index;
        return {
          article: transformItemArticleResponse(item?.price?.article) as Article,
          id: item?.id ?? 0,
          discount: discountItem.percentage,
          type: ProductNatureEnum.Advantage,
          originalPrice: getOriginalPrice(item?.price?.packingPrices, discountItem.type),
          discountedPrice: item?.price?.packingPrices
            ? getDiscountedPrice(
                item?.price?.packingPrices,
                discountItem.type,
                discountItem.percentage,
              )
            : 0,
          packingType: discountItem.type,
          // we dont have bundle quantity in the discount so we set it to 1
          quantity: 1,
          warehouse: item?.warehouse,
          warehouseId: item?.warehouseId,
          minOrderDiscountQuantity: cToNumber(discountItem?.minOrderDiscountQuantity),
        };
      });
    })
    .flat();
  return {
    data: dataToReturn,
    numberToAddToTotal,
  };
};

export function prepareDataForTableResumePositioningOrDiscount({
  dataResponse,
  isPositioningMode,
  isDiscountMode,
}: {
  dataResponse: DealItem[] | undefined;
  isDiscountMode: boolean;
  isPositioningMode: boolean;
}): IResumeTableResult[] {
  if (!dataResponse || isEmpty(dataResponse)) return [];

  return dataResponse.flatMap((response) => {
    const { discount, article, warehouse, originalPrice, packingType, discountedPrice } = response;
    const { pricesObject } = article;
    const priceObject = pricesObject?.[packingType as PackingTypeEnum];
    const baseObject: IResumeTableResult = {
      productName: article?.nameFr ?? '',
      discount: String(discount),
      priceWithoutDiscount: Number(originalPrice),
      priceWithDiscount: Number(discountedPrice),
      warehouse: warehouse?.nameFr ?? undefined,
      discountToDisplay: String(discount),
      shouldDisplayErrorPrice: isDiscountMode && Number(discount) <= 0,
      productQuantityOnUnit:
        extractPackingTypeLabel(packingType as PackingTypeEnum) ?? GlobalVariables.DashSeparator,
      productQuantityTotalOnUnit: GlobalVariables.DashSeparator,
    };
    const positioningDiscount = isDiscountMode
      ? priceObject?.discount?.filter((itemDiscount) => !itemDiscount.canShowTag)
      : undefined;
    // if positioningDiscount is defined, calculate the new discount and the new price with discount
    if (positioningDiscount && !isEmpty(positioningDiscount)) {
      const positioningDiscountResult: IResumeTableResult[] = getPositioningDiscountResumeResult({
        positioningDiscount,
        baseObject,
        priceWithoutDiscount: originalPrice,
        watchedPriceWithDiscount: discountedPrice,
      });

      // if positioningDiscountResult is not empty, return the baseObject and the newDiscount
      if (!isEmpty(positioningDiscountResult)) {
        return [baseObject, ...positioningDiscountResult];
      }
    }
    return [baseObject]; // Return only the original object if no newDiscount
  });
}
export const getDiscountOnPositioningResume = ({
  discountPositioning,
  baseObject,
  watchedPriceWithDiscount,
  priceWithoutDiscount,
}: {
  discountPositioning: Discount[];
  priceWithoutDiscount: number;
  watchedPriceWithDiscount: number;
  baseObject: IResumeTableResult;
}): IResumeTableResult[] =>
  discountPositioning.map((pd) => {
    const newDiscount = pd.percentage;
    const newPriceWithPositioning = getPriceAfterDiscount(
      Number(priceWithoutDiscount),
      Number(newDiscount),
    );

    const nameOfDiscountPositioning = [
      GlobalVariables.ParenthesisOpen,
      pd?.nameFr,
      GlobalVariables.ParenthesisClose,
    ].join('');

    return {
      ...baseObject,
      productName: `${baseObject.productName}${GlobalVariables.SpaceSeparator}${nameOfDiscountPositioning}`,
      warehouse: null,
      discountToDisplay: String(watchedPriceWithDiscount - newPriceWithPositioning),
    };
  });

export const getPositioningDiscountResumeResult = ({
  positioningDiscount,
  baseObject,
  priceWithoutDiscount,
  watchedPriceWithDiscount,
}: {
  positioningDiscount: Discount[];
  priceWithoutDiscount: number;
  watchedPriceWithDiscount: number;
  baseObject: IResumeTableResult;
}): IResumeTableResult[] =>
  positioningDiscount.map((pd) => {
    const newDiscount = pd.percentage;
    const newPriceWithPositioning = getPriceAfterDiscount(
      Number(priceWithoutDiscount),
      Number(newDiscount),
    );

    const nameOfPositioningDiscount = [
      GlobalVariables.ParenthesisOpen,
      pd?.nameFr,
      GlobalVariables.ParenthesisClose,
    ].join('');

    const newWarehouse =
      pd?.warehouse?.nameFr.concat(
        [GlobalVariables.DashSeparator, nameOfPositioningDiscount].join(`\n`),
      ) ?? undefined;

    const newDiscountToDisplay = getDiscountPercentage(
      Number(watchedPriceWithDiscount),
      Number(newPriceWithPositioning),
    );

    return {
      ...baseObject,
      discount: String(newDiscountToDisplay),
      priceWithoutDiscount: Number(newPriceWithPositioning),
      warehouse: newWarehouse,
      discountToDisplay: String(newDiscountToDisplay),
      shouldDisplayErrorPrice: newDiscountToDisplay <= 0,
    };
  });
export const transformDealPricesToTableResume = (
  response: IDealPricesListApi,
  stockBundle: number,
): IDiscountPricesForResume | undefined => {
  if (!response) return undefined;
  const data = response.results.map((res) => {
    const discount = compareAndTransformValue(res.discount, 0);
    const bundleQuantity = compareAndTransformValue(res.quantity, 1, 0);
    const priceWithoutDiscount = formattedDigitNumber(
      compareAndTransformValue(res.originalPrice, 0) * bundleQuantity,
    );
    const priceWithDiscount = res.discountedPrice * bundleQuantity;
    const packingUnitLabel = compareAndTransformValue(
      res.packingType ? LabelPcbEnum[res.packingType as PackingTypeEnum] : undefined,
      GlobalVariables.DoubleDash,
    );
    const productQuantityTotalOnUnit = `${stockBundle * bundleQuantity}${[
      GlobalVariables.SpaceSeparator,
      GlobalVariables.DashSeparator,
      GlobalVariables.SpaceSeparator,
    ].join('')}${packingUnitLabel}`;

    return {
      productName: res.article.nameFr,
      discount: String(discount),
      priceWithoutDiscount: priceWithoutDiscount,
      priceWithDiscount: priceWithDiscount,
      productQuantityTotalOnUnit: productQuantityTotalOnUnit,
      productQuantityOnUnit: `${bundleQuantity}${[
        GlobalVariables.SpaceSeparator,
        GlobalVariables.DashSeparator,
        GlobalVariables.SpaceSeparator,
      ].join('')}${packingUnitLabel}`,
      discountToDisplay: String(discount),
    };
  });
  return {
    lastPage: response?.lastPage,
    totalPerPage: response?.totalPerPage,
    nextPage: response?.nextPage,
    total: response?.total,
    previousPage: response?.previousPage,
    data,
  };
};
export const transformListDiscountPricesToTableResume = (
  data: IDiscountPricesListApi | undefined,
  isDiscountMode: boolean,
  isPositioningMode: boolean,
): IDiscountPricesForResume | undefined => {
  if (!data) return undefined;
  const { data: discountPrices, numberToAddToTotal } = transformDiscountPrices(data?.results);
  const discountPricesForResume = prepareDataForTableResumePositioningOrDiscount({
    dataResponse: discountPrices,
    isDiscountMode,
    isPositioningMode,
  });
  return {
    lastPage: data?.lastPage,
    totalPerPage: data?.totalPerPage,
    nextPage: data?.nextPage,
    total: (data?.total ?? 0) + numberToAddToTotal,
    previousPage: data?.previousPage,
    totalLengthPackingPrices: data?.totalLengthPackingPrices ?? 0,
    data: discountPricesForResume,
    totalAfterDiscount: data.totalAfterDiscount,
    totalBeforeDiscount: data.totalBeforeDiscount,
  };
};

export const transformListDealItems = (data: IDealPricesListApi): IDealPricesList | undefined => {
  if (!data) return undefined;
  return {
    lastPage: data?.lastPage,
    totalPerPage: data?.totalPerPage,
    nextPage: data?.nextPage,
    total: data?.total,
    previousPage: data?.previousPage,
    data: transformDealsItems(data?.results ?? []),
  };
};
export const transformListDiscountPrices = (
  data: IDiscountPricesListApi | undefined,
): IDiscountPackingPricesList | undefined => {
  if (!data) return undefined;
  const { data: discountPrices, numberToAddToTotal } = transformDiscountPrices(data?.results);
  return {
    lastPage: data?.lastPage,
    totalPerPage: data?.totalPerPage,
    nextPage: data?.nextPage,
    total: (data?.total ?? 0) + numberToAddToTotal,
    previousPage: data?.previousPage,
    totalLengthPackingPrices: data?.totalLengthPackingPrices ?? 0,
    data: discountPrices,
  };
};
export const transformDiscountsDetails = (data: IDetailsDiscountsApi): IDetailsDeal => {
  return {
    id: data?.id,
    nameAr: null,
    catalogs: [],
    status: data?.status === GlobalVariables.STATUS.ACTIVE,
    type: ProductTypeEnum.Deal,
    dealEndDate: data?.endDate,
    nameFr: data?.nameFr,
    name: data?.nameFr,
    dealRemainingStock: data?.discountRemainingStock,
    deletedAt: null,
    sageLabel: null,
    pricesObject: undefined,
    ean13Code: null,
    dealStock: data?.discountStock,
    dealStartDate: data?.startDate,
    dealType: DealTypeEnumApiValues.Discount,
    descriptionAr: null,
    descriptionFr: null,
    description: null,
    dealItems: transformDiscountPrices(data?.discountPrices).data,
    dealTargets: transformDealTarget(data?.targets),
    pcbsArray: [],
    sageReference: null,
    media: [],
    brand: null,
    dealWarehouse: null,
    discountWarehouses: data?.warehouses,
  };
};
